import { useEffect, useState } from "react";
import { Image as ImageIcon, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const ProofButton = ({ proofId }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  if (!proofId) return <span className="text-xs text-zinc-600">—</span>;
  const open = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/proofs/${proofId}`, { responseType: "blob" });
      setUrl(URL.createObjectURL(data));
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat bukti"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <button onClick={open} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[11px] hover:border-[#e5b80b]" data-testid={`proof-btn-${proofId}`}>
        {loading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />} Lihat
      </button>
      {url ? (
        <Dialog open onOpenChange={(o) => !o && setUrl(null)}>
          <DialogContent className="border-white/10 bg-[#0f0f14] text-white sm:max-w-lg" data-testid="proof-dialog">
            <DialogHeader>
              <DialogTitle>Bukti Transfer</DialogTitle>
              <DialogDescription className="text-zinc-500">Bukti upload belum berarti transaksi sah — periksa dulu.</DialogDescription>
            </DialogHeader>
            <img src={url} alt="Bukti transfer" className="max-h-[70vh] w-full rounded-lg object-contain" />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};

const NumField = ({ label, value, onChange, disabled, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} disabled={disabled} inputMode="numeric" className="rk-input mt-1" data-testid={testid} />
  </label>
);

export const BongkarConfigForm = ({ canManage }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/bongkar/config").then(({ data }) => setF({
      price_royal_dream: String(data.prices.royal_dream), price_higgs_domino: String(data.prices.higgs_domino),
      admin_fee_min: String(data.admin_fee_min), admin_fee_max: String(data.admin_fee_max),
      dest_royal_dream: data.dest_ids.royal_dream, dest_higgs_domino: data.dest_ids.higgs_domino,
    }));
  }, []);

  if (!f) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/bongkar-config", {
        price_royal_dream: Number(f.price_royal_dream || 0), price_higgs_domino: Number(f.price_higgs_domino || 0),
        admin_fee_min: Number(f.admin_fee_min || 0), admin_fee_max: Number(f.admin_fee_max || 0),
        dest_royal_dream: f.dest_royal_dream, dest_higgs_domino: f.dest_higgs_domino,
      });
      toast.success("Pengaturan bongkar disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rk-glass rounded-2xl p-6 lg:col-span-2" data-testid="bongkar-config-form">
      <h3 className="text-lg font-bold">Pengaturan Bongkar</h3>
      <p className="mt-1 text-xs text-zinc-500">Harga member, biaya admin, dan ID tujuan yang tampil di halaman /bongkar.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <NumField label="Harga member Royal Dream / 1B (Rp)" value={f.price_royal_dream} onChange={set("price_royal_dream")} disabled={!canManage} testid="cfg-price-rd" />
        <NumField label="Harga member Higgs Domino / 1B (Rp)" value={f.price_higgs_domino} onChange={set("price_higgs_domino")} disabled={!canManage} testid="cfg-price-hdi" />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <NumField label="Biaya admin min (Rp)" value={f.admin_fee_min} onChange={set("admin_fee_min")} disabled={!canManage} testid="cfg-fee-min" />
        <NumField label="Biaya admin maks (Rp)" value={f.admin_fee_max} onChange={set("admin_fee_max")} disabled={!canManage} testid="cfg-fee-max" />
        <NumField label="ID tujuan Royal Dream" value={f.dest_royal_dream} onChange={set("dest_royal_dream")} disabled={!canManage} testid="cfg-dest-rd" />
        <NumField label="ID tujuan Higgs Domino" value={f.dest_higgs_domino} onChange={set("dest_higgs_domino")} disabled={!canManage} testid="cfg-dest-hdi" />
      </div>
      {canManage ? (
        <button type="submit" disabled={busy} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm" data-testid="cfg-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan
        </button>
      ) : null}
    </form>
  );
};
